/**
 * F18 — Admin-consent step.
 *
 * Per Round 1 hard limit: NIPE may NEVER programmatically redirect to a
 * Microsoft URL. This component renders an `<a href={...}>` link that
 * the USER clicks. The wizard route owns the URL construction + the
 * sessionStorage intent stash; this component just renders the link.
 */

import { useState } from 'react';

export interface ConsentStepProps {
  tenantId: string;
  tenantDomain: string;
  tenantDisplayName: string | null;
  consentUrl: string;
  alreadyConsented: boolean;
  onContinue: () => void;
  onRestart: () => void;
}

export function ConsentStep({
  tenantId,
  tenantDomain,
  tenantDisplayName,
  consentUrl,
  alreadyConsented,
  onContinue,
  onRestart,
}: ConsentStepProps) {
  const [linkClicked, setLinkClicked] = useState(false);

  return (
    <section
      data-testid="onboard-consent-step"
      className="flex flex-col gap-4 p-4"
    >
      <h2 className="text-lg font-bold text-black">Grant admin consent</h2>
      <p className="text-sm text-black">
        Tenant:{' '}
        <strong>{tenantDisplayName ?? tenantDomain}</strong>{' '}
        <span className="text-xs font-mono text-gray-700">({tenantId})</span>
      </p>
      {alreadyConsented ? (
        <>
          <p
            data-testid="onboard-consent-already-granted"
            className="border border-[#080] bg-[#efe] p-3 text-sm text-black"
          >
            Admin consent already granted in this tenant.
          </p>
          <button
            type="button"
            data-testid="onboard-consent-continue"
            onClick={onContinue}
            className="self-start border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0]"
          >
            Continue
          </button>
        </>
      ) : (
        <>
          <p className="text-sm text-black">
            Repancy needs admin consent in your Microsoft 365 tenant. The
            link below will take you to Microsoft to sign in as a Global
            Admin and approve the requested permissions.
          </p>
          <a
            href={consentUrl}
            data-testid="onboard-consent-link"
            onClick={() => setLinkClicked(true)}
            className="self-start border border-[#404040] bg-[#d0d0d0] px-4 py-1 text-sm font-bold text-black cursor-pointer hover:bg-[#e0e0e0] no-underline"
          >
            Open Microsoft admin consent →
          </a>
          {linkClicked && (
            <p className="text-xs text-gray-700">
              After approving in Microsoft, you'll land back here automatically.
            </p>
          )}
          <button
            type="button"
            data-testid="onboard-consent-restart"
            onClick={onRestart}
            className="self-start border border-[#404040] bg-[#d0d0d0] px-3 py-1 text-xs text-black hover:bg-[#e0e0e0]"
          >
            ← Restart
          </button>
        </>
      )}
    </section>
  );
}
